/**
 * Screen-share state helpers.
 *
 * Lives apart from the indicator components so that file only exports
 * components (react-refresh/only-export-components).
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCallStateHooks } from "@stream-io/video-react-sdk";
import { useDeviceAction } from "@/features/call/hooks/useDeviceAction";
import {
  getScreenShareSource,
  screenShareSurfaceKey,
} from "@/features/call/utils/screenShareSource";

/** SfuModels.TrackType.SCREEN_SHARE */
export const SCREEN_SHARE_TRACK_TYPE = 3;

/**
 * Describes the local screen share: which surface was picked, and a
 * one-call-away way to stop it.
 */
export const useLocalScreenShare = () => {
  const { t } = useTranslation();
  const { useScreenShareState } = useCallStateHooks();
  const { screenShare, mediaStream, isMute } = useScreenShareState();

  const source = useMemo(() => getScreenShareSource(mediaStream), [mediaStream]);

  // getDisplayMedia rejects when the picker is dismissed or sharing is blocked;
  // useDeviceAction keeps that from escaping the click handler and ignores a
  // second click while the picker is still open.
  const { run: toggle, busy: isToggling } = useDeviceAction("screenShare", () =>
    screenShare.toggle(),
  );

  const surfaceLabel = t(screenShareSurfaceKey(source?.surface));
  // Prefer the real window/tab title when the browser gives us one,
  // otherwise fall back to the surface kind ("Entire screen", "A browser tab").
  const sourceLabel = source?.label
    ? `${surfaceLabel} — ${source.label}`
    : surfaceLabel;

  return {
    isSharing: !isMute && !!mediaStream,
    mediaStream,
    surfaceLabel,
    sourceLabel,
    toggle,
    isToggling,
    stop: toggle,
  };
};

/** The participant currently publishing a screen share, if any */
export const usePresentingParticipant = () => {
  const { useParticipants } = useCallStateHooks();
  const participants = useParticipants();
  return participants.find((p) =>
    p.publishedTracks?.includes(SCREEN_SHARE_TRACK_TYPE),
  );
};
